import React, { useMemo } from 'react';

import { ContactItemDto } from '@/api/generated';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { CustomTableFilterDefinition } from '@/components/table/components/formBuilder/model/CustomTableFilterDefinition.model.ts';
import { CustomTableFilterTypeEnum } from '@/components/table/components/formBuilder/model/CustomTableFilterTypeEnum.ts';
import { CustomTable, CustomTableColumn } from '@/components/table/CustomTable.comp.tsx';
import { useDictionariesContext } from '@/contexts/dictionaries/Dictionaries.context.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { TableBasicProps } from '@/models/TableBasicProps.model.ts';
import { useGetSelectOptions } from '@/models/useGetSelectOptions.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { ContactsHeaderAdd } from '@/views/contacts/components/header/components/ContactsHeaderAdd.comp.tsx';
import { ContactsTableRow } from '@/views/contacts/components/table/components/ContactsTableRow.comp.tsx';
import { ContactsSearchFormModel } from '@/views/contacts/components/table/model/ContactsSearchForm.model.ts';
import {
  ContactsTableState,
  initialContactsTableState,
} from '@/views/contacts/components/table/model/ContactsTableState.model.ts';
import { useContactsFormValidation } from '@/views/contacts/utils/useContactsForm.validation.ts';
import { useGetContacts } from '@/views/contacts/utils/useGetContacts.util.ts';

type Props = {
  isAlreadyInCustomer?: SelectSearchOptionModel<boolean>;
  isOnCustomerDetails?: boolean;
  isOnSupplierDetails?: boolean;
  urlStateEnabled?: boolean;
} & TableBasicProps<ContactItemDto, ContactsSearchFormModel>;

export const ContactsTable: React.FC<Props> = props => {
  const {
    actionsColumns,
    additionalButtons,
    customRow,
    hidePaper,
    isAlreadyInCustomer,
    isOnCustomerDetails,
    isOnSupplierDetails,
    onCustomAction,
    pageSize,
    request,
    tableKey,
    tableSx,
    urlStateEnabled,
  } = props;
  const { t } = useTranslationWithPrefix('views.contacts.table');
  const { getContacts, isLoading } = useGetContacts();
  const validation = useContactsFormValidation();
  const { booleanOptions, contactStatusOptions } = useGetSelectOptions();
  const { USERS_SHORT } = useDictionariesContext();
  const { userSelectOption } = useUserContext();

  const filters: CustomTableFilterDefinition<ContactsSearchFormModel>[] = useMemo(() => {
    const filter: CustomTableFilterDefinition<ContactsSearchFormModel>[] = [
      {
        label: t('head.fullTextSearchInput'),
        name: 'fullTextSearchInput',
        type: CustomTableFilterTypeEnum.Text,
        xs: 3,
      },
      {
        label: t('head.customerFullTextSearchInput'),
        name: 'customerFullTextSearchInput',
        type: CustomTableFilterTypeEnum.Text,
        xs: 3,
      },
      {
        label: t('head.lastContactedTo'),
        name: 'lastContactedTo',
        type: CustomTableFilterTypeEnum.Date,
        xs: 3,
      },
    ];

    if (!isOnSupplierDetails) {
      filter.push(
        {
          isMulti: true,
          label: t('head.ownerIds'),
          name: 'ownerIds',
          options: USERS_SHORT,
          type: CustomTableFilterTypeEnum.Select,
          xs: 3,
        },
        {
          label: t('head.status'),
          name: 'status',
          options: contactStatusOptions,
          type: CustomTableFilterTypeEnum.Select,
          xs: 3,
        },
      );
    }

    if (!isOnSupplierDetails && !isOnCustomerDetails) {
      filter.push({
        label: t('head.isAlreadyInCustomer'),
        name: 'isAlreadyInCustomer',
        options: booleanOptions,
        type: CustomTableFilterTypeEnum.Select,
        xs: 3,
      });
      // filter.push({
      //   label: t('head.isAlreadyInSupplier'),
      //   name: 'isAlreadyInSupplier',
      //   options: booleanOptions,
      //   type: CustomTableFilterTypeEnum.Select,
      //   xs: 3,
      // });
    }
    return filter;
  }, [
    USERS_SHORT,
    booleanOptions,
    contactStatusOptions,
    isOnCustomerDetails,
    isOnSupplierDetails,
    t,
  ]);

  const tableHead: CustomTableColumn<string>[] = useMemo(() => {
    const head = [
      {
        id: 'lastName',
        label: t('body.lastName'),
        sx: { width: '15%' },
      },
      {
        id: 'firstName',
        label: t('body.firstName'),
        sx: { width: '10%' },
      },
      {
        id: 'name',
        label: t('body.name'),
        sx: { width: '15%' },
      },
      {
        id: 'customer',
        label: t('body.customer'),
        sx: { width: '15%' },
      },
      {
        id: 'contactOwnerId',
        label: t('body.contactOwnerId'),
        sx: { width: '15%' },
      },
      {
        id: 'lastContactedAt',
        label: t('body.lastContactedAt'),
        sx: { width: '10%' },
      },
      {
        id: 'status',
        label: t('body.status'),
        sx: { width: '10%' },
      },
    ];
    if (actionsColumns) {
      head.push({
        id: 'actions',
        label: '',
        sx: { width: '5%' },
      });
    }
    return head;
  }, [actionsColumns, t]);

  const req = request ?? getContacts;

  const init = async (initialParams: ContactsTableState) => {
    return await req(initialParams);
  };

  return (
    <CustomTable<string, ContactsSearchFormModel, ContactItemDto>
      additionalButtons={[...(additionalButtons ?? []), { button: AddContactButton, name: 'add' }]}
      CustomTableRow={customRow ?? ContactsTableRow}
      customTableRowAction={onCustomAction}
      filters={filters}
      getTableDataRequest={req}
      hidePaper={hidePaper}
      idKey="contactId"
      init={init}
      initParams={{
        ...initialContactsTableState({ isAlreadyInCustomer }),
        pageSize: pageSize ?? 10,
        params: {
          ...initialContactsTableState({ isAlreadyInCustomer }).params,
          ownerIds: [userSelectOption],
        },
      }}
      isLoading={isLoading}
      size="small"
      tableHead={tableHead}
      tableKey={tableKey}
      tableSx={tableSx}
      urlStateEnabled={urlStateEnabled}
      validation={validation}
    />
  );
};

const AddContactButton = () => {
  return <ContactsHeaderAdd />;
};
